import React from "react";
import { Table, TableBody, TableCell, TableContainer, TableRow, Typography, Paper, TableHead, Box, Radio } from "@mui/material";
import { ClickableIcon } from "../ui/Icon";
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { ClipText } from "../ui/ClipText";
import Alert from "@mui/material/Alert/Alert";

interface DataInfoTableProps {
  title?: string;
  data?: Record<string, string>;
  onEdit?: () => void;
}

export const DataInfoTable = (props: DataInfoTableProps) => {
  const { title = "", data } = props;

  return (
    <TableContainer component={Paper} elevation={4} sx={{ width: "100%", maxWidth: "100%", p: 2, overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: { xs: "100%", md: 200 } }}><Typography variant="h6">{title}</Typography></TableCell>
            <TableCell sx={{ width: { xs: "100%", md: 400 } }}>
              {props.onEdit ?
                <ClickableIcon title={"Rediger"} onClick={props.onEdit}>
                  <EditIcon />
                </ClickableIcon> :
                null
              }
            </TableCell>
          </TableRow>
        </TableHead>
        {data && (
          <TableBody>
            {Object.entries(data).map(([key, value]) => (
              <TableRow key={key}>
                <TableCell sx={{ fontWeight: "bold" }}><ClipText>{key}</ClipText></TableCell>
                <TableCell><ClipText>{value ? value : "-"}</ClipText></TableCell>
              </TableRow>
            ))}
          </TableBody>
        )}
      </Table>
    </TableContainer>
  );
};

interface Item {
  id: number;
  [key: string]: any;
}

interface DataListInfoTableProps {
  title: string;
  data: Item[];
  displayField: string;
  onAdd?: () => void;
  onEdit?: (id: number) => void;
  onDelete?: (id: number) => void;
  onPrimaryCahnge?: (id: number) => void;
  primaryField?: string;
}

export const DataListInfoTable = (props: DataListInfoTableProps) => {
  const { title, data, displayField, primaryField } = props;

  if (props.onPrimaryCahnge) {
    if (!primaryField) {
      throw new Error("primaryField is not set when onPrimaryChange is used");
    }

    if (data.length > 0) {
      if (typeof data[0][primaryField] !== "boolean") {
        throw new Error(`Field "${primaryField}" is not boolean, got: ${typeof data[0][primaryField]}`);
      }
    }
  }

  return (
    <TableContainer component={Paper} elevation={4} sx={{ width: "100%", maxWidth: "100%", p: 2, overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: "100%" }}><Typography variant="h6">{title}</Typography></TableCell>
            {props.onPrimaryCahnge &&
              <TableCell>Primær</TableCell>
            }
            <TableCell sx={{ width: { xs: "100%", md: 400 } }}>
              {props.onAdd &&
                <ClickableIcon title={"Legg til"} size="small" onClick={props.onAdd}>
                  <AddIcon />
                </ClickableIcon>
              }
            </TableCell>
          </TableRow>
        </TableHead>
        {data.length > 0 && (
          <TableBody>
            {data.map((item) => (
              <TableRow key={item.id}>
                <TableCell><ClipText>{item[displayField]}</ClipText></TableCell>
                {props.onPrimaryCahnge &&
                  <TableCell>
                    <Radio checked={item[primaryField]} onChange={() => props.onPrimaryCahnge(item.id)} />
                  </TableCell>
                }
                <TableCell sx={{ width: { xs: "100%", md: 400 } }}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {props.onEdit &&
                      <ClickableIcon title={"Rediger"} size="small" onClick={() => props.onEdit(item.id)}>
                        <EditIcon />
                      </ClickableIcon>
                    }
                    {props.onDelete &&
                      <ClickableIcon title={"Slett"} size="small" onClick={() => props.onDelete(item.id)}>
                        <DeleteIcon color={"error"} />
                      </ClickableIcon>
                    }
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        )}
      </Table>
      {data.length === 0 && (
        <Alert severity="info" sx={{ width: "100%" }}>Ingen data å vise</Alert>
      )}
    </TableContainer>
  );
};